<template>
  <section class="px-3 pb-3">
    <AdminToastStack :toasts="toasts" @close="removeToast" />

    <div class="admin-surface">
      <div class="admin-surface__head">
        <span class="admin-surface__title">Инвайты</span>
        <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadInvites">
          <i class="bi bi-arrow-repeat me-1"></i>
          Обновить
        </button>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 px-3 mt-3 mb-0">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2 px-3 mt-3 mb-0">{{ successMessage }}</div>

      <div class="row g-3 mt-1">
        <div class="col-12 col-xxl-8">
          <div class="row g-2 mb-2">
            <div class="col-12 col-md-6">
              <input
                v-model.trim="searchQuery"
                type="search"
                class="form-control form-control-sm"
                placeholder="Поиск по коду, email, заметке"
              />
            </div>
            <div class="col-12 col-md-6">
              <select v-model="statusFilter" class="form-select form-select-sm">
                <option value="all">Все статусы</option>
                <option value="active">Активные</option>
                <option value="used">Использованные</option>
                <option value="revoked">Отозванные</option>
              </select>
            </div>
          </div>

          <div class="table-responsive admin-table-wrap">
            <table class="table table-sm align-middle mb-0 admin-table">
              <thead>
                <tr>
                  <th>Код</th>
                  <th>Статус</th>
                  <th>Email пользователя</th>
                  <th>Использован</th>
                  <th>Заметка</th>
                  <th>Кем создан</th>
                  <th>Создан</th>
                  <th class="text-end">Действия</th>
                </tr>
              </thead>
              <tbody v-if="filteredInvites.length">
                <tr v-for="invite in filteredInvites" :key="invite.id">
                  <td><code>{{ invite.code }}</code></td>
                  <td>
                    <span class="badge" :class="statusBadgeClass(invite)">{{ statusLabel(invite) }}</span>
                  </td>
                  <td>{{ invite.usedByEmail || '—' }}</td>
                  <td>{{ invite.usedAt ? formatDateTime(invite.usedAt) : '—' }}</td>
                  <td>{{ invite.note || '—' }}</td>
                  <td>{{ invite.createdBy || '—' }}</td>
                  <td>{{ formatDateTime(invite.createdAt) }}</td>
                  <td class="text-end">
                    <button
                      v-if="invite.active && !invite.used"
                      class="btn btn-outline-danger btn-sm"
                      :disabled="isLoading"
                      @click="revokeInvite(invite)"
                    >
                      Отозвать
                    </button>
                    <span v-else class="text-muted">—</span>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="8" class="text-center text-muted py-3">Инвайты не найдены.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-12 col-xxl-4">
          <div class="admin-detail">
            <div class="admin-detail__title">Создать инвайты</div>
            <small class="text-muted">Коды генерируются автоматически.</small>

            <form class="mt-2" @submit.prevent="submitForm">
              <div class="row g-2">
                <div class="col-12">
                  <label class="form-label mb-1">Количество (1–50)</label>
                  <input v-model.number="form.count" type="number" min="1" max="50" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Заметка</label>
                  <input v-model.trim="form.note" type="text" class="form-control form-control-sm" placeholder="Необязательно" />
                </div>
              </div>

              <div class="d-flex justify-content-end mt-3">
                <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">Создать</button>
              </div>
            </form>

            <div v-if="createdCodes.length" class="created-codes mt-3">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <span class="fw-semibold">Созданные коды</span>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="copyCreatedCodes">
                  <i class="bi bi-clipboard me-1"></i>
                  Копировать
                </button>
              </div>
              <ul class="list-unstyled mb-0">
                <li v-for="code in createdCodes" :key="code"><code>{{ code }}</code></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import AdminToastStack from '@/admin/components/AdminToastStack.vue'
import { useAdminRequestState } from '@/admin/composables/useAdminRequestState'
import { createAdminInvites, getAdminInvites, revokeAdminInvite } from '@/admin/api/adminService'
import { formatDateTime, normalizeSearchText } from '@/admin/utils/adminApiUtils'

const invites = ref([])
const searchQuery = ref('')
const statusFilter = ref('all')
const createdCodes = ref([])

const form = reactive({
  count: 1,
  note: '',
})

const {
  isLoading,
  errorMessage,
  successMessage,
  toasts,
  removeToast,
  clearMessages,
  pushError,
  pushSuccess,
  execute,
} = useAdminRequestState()

const matchesStatus = invite => {
  switch (statusFilter.value) {
    case 'active':
      return invite.active && !invite.used
    case 'used':
      return invite.used
    case 'revoked':
      return !invite.active && !invite.used
    default:
      return true
  }
}

const filteredInvites = computed(() => {
  const query = normalizeSearchText(searchQuery.value)
  return invites.value.filter(invite => {
    if (!matchesStatus(invite)) return false
    if (!query) return true
    const haystack = `${invite.code} ${invite.usedByEmail || ''} ${invite.note || ''}`.toLowerCase()
    return haystack.includes(query)
  })
})

const statusLabel = invite => {
  if (invite.used) return 'Использован'
  if (!invite.active) return 'Отозван'
  return 'Активен'
}

const statusBadgeClass = invite => {
  if (invite.used) return 'text-bg-secondary'
  if (!invite.active) return 'text-bg-danger'
  return 'text-bg-success'
}

const loadInvites = async () => {
  clearMessages()
  const list = await execute(() => getAdminInvites(), {
    error: 'Не удалось получить список инвайтов.',
  })
  invites.value = list
}

const submitForm = async () => {
  clearMessages()
  const count = Math.trunc(Number(form.count) || 0)
  if (count < 1 || count > 50) {
    pushError('Количество должно быть в диапазоне 1–50.')
    return
  }

  const created = await execute(() => createAdminInvites({ count, note: form.note }), {
    success: `Создано инвайтов: ${count}.`,
    error: 'Не удалось создать инвайты.',
  })

  createdCodes.value = (created || []).map(invite => invite.code)
  form.note = ''
  form.count = 1
  await loadInvites()
}

const revokeInvite = async invite => {
  const confirmed = window.confirm(`Отозвать инвайт ${invite.code}?`)
  if (!confirmed) return

  clearMessages()
  await execute(() => revokeAdminInvite(invite.id), {
    success: 'Инвайт отозван.',
    error: 'Не удалось отозвать инвайт.',
  })
  await loadInvites()
}

const copyCreatedCodes = async () => {
  const text = createdCodes.value.join('\n')
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    pushSuccess('Коды скопированы в буфер обмена.')
  } catch {
    pushError('Не удалось скопировать коды.')
  }
}

onMounted(async () => {
  await loadInvites()
})
</script>

<style scoped>
.admin-surface {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.admin-surface__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-surface__title {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
}

.admin-table-wrap {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  max-height: 72vh;
  overflow: auto;
}

.admin-table thead th {
  position: sticky;
  top: 0;
  background: #f8fbff;
  z-index: 2;
  color: #607089;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e9edf6;
}

.admin-detail {
  border: 1px solid #e4ebf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px;
}

.admin-detail__title {
  color: #1f2d45;
  font-weight: 700;
}

.created-codes code {
  font-size: 14px;
}
</style>
